import type {
  BaseOptions,
  BasePaginatedOptions,
  BaseResult,
  CombineService,
  LoadMoreFormatReturn,
  LoadMoreOptions,
  LoadMoreOptionsWithFormat,
  LoadMoreParams,
  LoadMoreResult,
  OptionsWithFormat,
  PaginatedFormatReturn,
  PaginatedOptionsWithFormat,
  PaginatedParams,
  PaginatedResult
} from "@ahooksjs/use-request/lib/types";
import { useRequest as useRequestOriginal } from "ahooks";
import { message } from "antd";

// Overload for basic useRequest
function useRequest<R = any, P extends any[] = any>(
  service: CombineService<R, P>,
  options?: BaseOptions<R, P>
): BaseResult<R, P>;

// Overload for useRequest with formatResult
function useRequest<
  R = any,
  P extends any[] = any,
  U = any,
  UU extends U = any
>(
  service: CombineService<R, P>,
  options: OptionsWithFormat<R, P, U, UU>
): BaseResult<U, P>;

// Overload for paginated useRequest with formatResult
function useRequest<R = any, Item = any, U extends Item = any>(
  service: CombineService<R, PaginatedParams>,
  options: PaginatedOptionsWithFormat<R, Item, U>
): PaginatedResult<Item>;

// Overload for paginated useRequest without formatResult
function useRequest<R = any, Item = any, U extends Item = any>(
  service: CombineService<PaginatedFormatReturn<Item>, PaginatedParams>,
  options: BasePaginatedOptions<U>
): PaginatedResult<Item>;

// Overload for loadMore useRequest with formatResult
function useRequest<R extends LoadMoreFormatReturn, RR>(
  service: CombineService<RR, LoadMoreParams<R>>,
  options: LoadMoreOptionsWithFormat<R, RR>
): LoadMoreResult<R>;

// Overload for loadMore useRequest without formatResult
function useRequest<R extends LoadMoreFormatReturn, RR extends R>(
  service: CombineService<R, LoadMoreParams<R>>,
  options: LoadMoreOptions<RR>
): LoadMoreResult<R>;

// Implementation
function useRequest<R = any, P extends any[] = any>(
  service: any,
  options?: any
): any {
  const defaultOnError = (err: Error, params: any) => {
    message.error(err.message || "Алдаа гарлаа");
  };

  const mergedOptions = options
    ? {
      ...options,
      onError: options.onError || defaultOnError,
    }
    : {
      onError: defaultOnError,
    };

  return useRequestOriginal(service, mergedOptions);
}

export default useRequest;
